/**
 * Read-only audit-trail view for vocabulary derivation runs (CZNA-365).
 *
 * Renders `vocabulary_derivation_run` rows returned by
 * `GET .../derivation-rules/runs` as a compact MultiColumnList. Mirrors
 * `RuleHistoryView.tsx`. Usable both embedded in the rule detail pane
 * (`ruleId` prop, per-rule history) and — if a caller omits `ruleId` — as a
 * global "all runs" view (not wired to a standalone route by this ticket; the
 * minimum bar is per-rule embedding, see `DerivationRuleDetail`).
 *
 * `triggeredBy` is either a FOLIO user id (UUID) or a literal like "MANUAL" /
 * "IMPORT". Only UUID-shaped values are sent to `useUserName` — literals are
 * rendered as-is, avoiding a doomed lookup request per row.
 */
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { MultiColumnList, Loading, MessageBanner } from '@folio/stripes/components';
import { useVocabularyDerivationRuns } from '../../hooks/useVocabularyDerivationRules';
import useUserName from '../../hooks/useUserName';
var UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
var TriggeredByCell = function (_a) {
    var triggeredBy = _a.triggeredBy;
    var isUuid = UUID_RE.test(triggeredBy);
    var name = useUserName(isUuid ? triggeredBy : null).name;
    if (!isUuid)
        return React.createElement(React.Fragment, null, triggeredBy);
    return React.createElement(React.Fragment, null, name || triggeredBy);
};
var RunHistoryView = function (_a) {
    var ruleId = _a.ruleId, enabled = _a.enabled;
    var intl = useIntl();
    var _b = useVocabularyDerivationRuns({ ruleId: ruleId, limit: 100 }, enabled), data = _b.data, isLoading = _b.isLoading, isError = _b.isError;
    var columnMapping = useMemo(function () { return ({
        startedAt: React.createElement(FormattedMessage, { id: "ui-czna-authorities.derivationRules.runs.column.startedAt" }),
        triggeredBy: React.createElement(FormattedMessage, { id: "ui-czna-authorities.derivationRules.runs.column.triggeredBy" }),
        distinctCandidates: React.createElement(FormattedMessage, { id: "ui-czna-authorities.derivationRules.runs.column.distinctCandidates" }),
        termsCreated: React.createElement(FormattedMessage, { id: "ui-czna-authorities.derivationRules.runs.column.termsCreated" }),
        termsAlreadyPresent: React.createElement(FormattedMessage, { id: "ui-czna-authorities.derivationRules.runs.column.termsAlreadyPresent" }),
        componentsRematched: React.createElement(FormattedMessage, { id: "ui-czna-authorities.derivationRules.runs.column.componentsRematched" }),
        termsNoLongerQualifying: React.createElement(FormattedMessage, { id: "ui-czna-authorities.derivationRules.runs.column.termsNoLongerQualifying" }),
        dryRun: React.createElement(FormattedMessage, { id: "ui-czna-authorities.derivationRules.runs.column.dryRun" }),
        thresholdDesc: React.createElement(FormattedMessage, { id: "ui-czna-authorities.derivationRules.runs.column.thresholdDesc" }),
        sourcesDesc: React.createElement(FormattedMessage, { id: "ui-czna-authorities.derivationRules.runs.column.sourcesDesc" }),
    }); }, []);
    var formatter = useMemo(function () { return ({
        startedAt: function (r) { return "".concat(intl.formatDate(r.startedAt, { day: '2-digit', month: '2-digit', year: 'numeric' }), " ").concat(intl.formatTime(r.startedAt, { hour: '2-digit', minute: '2-digit' })); },
        triggeredBy: function (r) { return React.createElement(TriggeredByCell, { triggeredBy: r.triggeredBy }); },
        distinctCandidates: function (r) { return r.distinctCandidates; },
        termsCreated: function (r) { return r.termsCreated; },
        termsAlreadyPresent: function (r) { return r.termsAlreadyPresent; },
        componentsRematched: function (r) { return r.componentsRematched; },
        // CZNA-811 — null means the run never measured it (recorded before the column, or
        // failed); 0 is a measurement. The em dash keeps the two apart, as for sourcesDesc.
        termsNoLongerQualifying: function (r) { var _a; return (_a = r.termsNoLongerQualifying) !== null && _a !== void 0 ? _a : '—'; },
        dryRun: function (r) { return (React.createElement("span", { "data-testid": "run-dry-run-".concat(r.id), style: {
                display: 'inline-block',
                backgroundColor: r.dryRun ? '#fbf1e6' : '#e6f4ea',
                color: r.dryRun ? '#8a5a1c' : '#1e7e34',
                borderRadius: '10px',
                padding: '0 8px',
                fontSize: '0.8em',
                fontWeight: 600,
                whiteSpace: 'nowrap',
            } }, intl.formatMessage({
            id: r.dryRun
                ? 'ui-czna-authorities.derivationRules.runs.dryRun.yes'
                : 'ui-czna-authorities.derivationRules.runs.dryRun.no',
        }))); },
        thresholdDesc: function (r) { return r.thresholdDesc; },
        // CZNA-565 — runs recorded before the column existed genuinely do not know which
        // sources they read; an em dash says so rather than implying the rule's current list.
        sourcesDesc: function (r) { return r.sourcesDesc || '—'; },
    }); }, [intl]);
    if (!enabled)
        return null;
    if (isLoading)
        return React.createElement(Loading, null);
    if (isError) {
        return (React.createElement(MessageBanner, { type: "error" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.derivationRules.runs.loadError" })));
    }
    var runs = data !== null && data !== void 0 ? data : [];
    if (runs.length === 0) {
        return (React.createElement("p", { style: { color: '#666' } },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.derivationRules.runs.empty" })));
    }
    return (React.createElement(MultiColumnList, { contentData: runs, columnMapping: columnMapping, visibleColumns: ['startedAt', 'triggeredBy', 'distinctCandidates', 'termsCreated', 'termsAlreadyPresent', 'componentsRematched', 'termsNoLongerQualifying', 'dryRun', 'thresholdDesc', 'sourcesDesc'], formatter: formatter, interactive: false }));
};
export default RunHistoryView;
